import { Button, Dialog, BhiFormDialog, TextInput } from 'src/UIKit';
import { CodeBlock } from '../CodeBlock';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import React, { FC, Fragment, useEffect, useState } from 'react';
import SelectWithSearch from 'src/components/SelectWithSearch/view';
import { addApikeyValidationSchema, ValidationErrors } from 'src/constants/validations';
import * as Yup from 'yup';
import { AddApiKeyModalController } from '../../controllers';
import { ApikeyFormData } from '../../types';

const emptyFormValues: ApikeyFormData = {
  name: '',
  permissions: [],
  description: '',
};

interface AddApiKeyModalProps extends AddApiKeyModalController {}

export const AddApiKeyModal: FC<AddApiKeyModalProps> = props => {
  const { isOpen, initFormData, permissions, createdToken, close, submit, closeTokenModal } = props;
  const [formValues, setFormValues] = useState<ApikeyFormData>(emptyFormValues);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const validateFormSync = (values: ApikeyFormData) => {
    try {
      addApikeyValidationSchema.validateSync(values, { abortEarly: false });
      return false;
    } catch {
      return true;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newValues = { ...formValues, [name]: value };
    setFormValues(newValues);
    setIsButtonDisabled(validateFormSync(newValues));
  };

  const handleSelectChange = (nextPermissions: string[]) => {
    const newValues = { ...formValues, permissions: nextPermissions };
    setFormValues(newValues);
    setIsButtonDisabled(validateFormSync(newValues));
  };

  const validate = async (values: ApikeyFormData) => {
    try {
      await addApikeyValidationSchema.validate(values, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors = err.inner?.reduce((acc, curr) => {
          if (curr.path) {
            acc[curr.path] = curr?.message;
          }
          return acc;
        }, {} as ValidationErrors);
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async () => {
    const isValid = await validate(formValues);
    if (!isValid) {
      return;
    }

    const result = await submit(formValues);
    if (result?.nameError) {
      setErrors({ name: result.nameError });
    }
    if (result?.validationError) {
      setErrors({ validation: result.validationError });
    }
  };

  const handleClose = () => {
    setFormValues(emptyFormValues);
    setErrors({});
    setIsButtonDisabled(true);
    close();
  };

  useEffect(() => {
    if (initFormData) {
      setFormValues(initFormData);
      setIsButtonDisabled(validateFormSync(initFormData));
      setErrors({});
    }
  }, [initFormData]);

  if (!permissions.length) {
    return null;
  }

  return (
    <Fragment>
      <BhiFormDialog
        onClose={handleClose}
        open={isOpen}
        title={initFormData?.id ? 'Edit API Key' : 'Add API Key'}
        error={errors.validation}
        actions={
          <>
            <Button variant="outlined" onClick={handleClose}>Cancel</Button>
            <Button variant="contained" disabled={isButtonDisabled} onClick={handleSubmit}>
              {initFormData?.id ? 'Update API Key' : 'Create API Key'}
            </Button>
          </>
        }
      >
        <TextInput
          label="Name"
          name="name"
          margin="none"
          size="small"
          value={formValues.name}
          onChange={handleChange}
          error={!!errors.name}
          helperText={errors.name}
        />
        <TextInput
          label="Description"
          name="description"
          margin="none"
          size="small"
          value={formValues.description}
          onChange={handleChange}
          error={!!errors.description}
          helperText={errors.description}
        />
        <SelectWithSearch
          label="Permissions"
          placeholder=""
          size="small"
          options={permissions.map(permission => ({
            text: permission.description,
            value: permission.name,
          }))}
          values={formValues.permissions || []}
          handleSelectChange={(e, items) => handleSelectChange(items.map(item => item.value))}
          error={!!errors.permissions}
          helperText={errors.permissions}
        />
        {initFormData?.token && (
          <section aria-label="API Key">
            <Typography variant="caption" component="p" color="text.secondary" mb={1}>
              API Key
            </Typography>
            <CodeBlock as="div">{initFormData.token}</CodeBlock>
          </section>
        )}
      </BhiFormDialog>

      <Dialog open={!!createdToken} onClose={closeTokenModal} title="API Key" fullWidth maxWidth="sm">
        <DialogContent>
          <CodeBlock as="div">{createdToken}</CodeBlock>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};
